/**
 * Helper functions for message-related reducer logic.
 * Handles message state mutations in chat slice reducers.
 */

import type {
  AddOptimisticMessagePayload,
  AppendMessageStreamContentPayload,
  ChatMessageEntity,
  CompleteMessageStreamingPayload,
  ConfirmOptimisticMessagePayload,
  FailMessageStreamingPayload,
  RemoveOptimisticMessagePayload,
  StartMessageStreamingPayload,
} from './chat-message-entity.interface'
import type { ChatState } from './chat-state.interface'

export const handleAddOptimisticMessage = (
  state: ChatState,
  payload: AddOptimisticMessagePayload
): void => {
  const { conversationId, message } = payload

  if (state.messageEntities[message.id]) {
    return
  }

  state.messageEntities[message.id] = message

  if (!state.messageIdsByConversation[conversationId]) {
    state.messageIdsByConversation[conversationId] = []
  }

  const idList = state.messageIdsByConversation[conversationId]
  if (!idList.includes(message.id)) {
    idList.push(message.id)
  }
}

export const handleConfirmOptimisticMessage = (
  state: ChatState,
  payload: ConfirmOptimisticMessagePayload
): void => {
  const { conversationId, optimisticId, realId, attachmentList } = payload
  const message = state.messageEntities[optimisticId]
  if (!message) return

  const updatedMessage: ChatMessageEntity =
    message.type === 'message'
      ? {
          ...message,
          id: realId,
          isPending: false,
          attachmentList: attachmentList?.length ? attachmentList : message.attachmentList,
        }
      : { ...message, id: realId, isPending: false }

  delete state.messageEntities[optimisticId]
  state.messageEntities[realId] = updatedMessage

  const idList = state.messageIdsByConversation[conversationId]
  if (idList) {
    const index = idList.indexOf(optimisticId)
    if (index !== -1) {
      idList[index] = realId
    }
  }
}

export const handleRemoveOptimisticMessage = (
  state: ChatState,
  payload: RemoveOptimisticMessagePayload
): void => {
  const { conversationId, messageId } = payload
  delete state.messageEntities[messageId]

  const idList = state.messageIdsByConversation[conversationId]
  if (idList) {
    state.messageIdsByConversation[conversationId] = idList.filter((id) => id !== messageId)
  }
}

export const handleStartMessageStreaming = (
  state: ChatState,
  payload: StartMessageStreamingPayload
): void => {
  const { messageId } = payload
  const message = state.messageEntities[messageId]
  if (message) {
    message.isStreaming = true
    message.streamingContent = ''
  }
}

export const handleAppendMessageStreamContent = (
  state: ChatState,
  payload: AppendMessageStreamContentPayload
): void => {
  const { messageId, content } = payload
  const message = state.messageEntities[messageId]
  if (message) {
    message.streamingContent += content
  }
}

export const handleCompleteMessageStreaming = (
  state: ChatState,
  payload: CompleteMessageStreamingPayload
): void => {
  const { messageId } = payload
  const message = state.messageEntities[messageId]
  if (!message) return
  message.content = message.streamingContent || message.content
  message.isStreaming = false
  message.streamingContent = ''
}

export const handleFailMessageStreaming = (
  state: ChatState,
  payload: FailMessageStreamingPayload
): void => {
  const { messageId, errorMessage } = payload
  const message = state.messageEntities[messageId]
  if (!message) return
  message.isStreaming = false
  message.errorMessage = errorMessage
}
